import React, { useEffect, useState } from "react";
import api, { fmt, monthISO } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card, PageHeader, Empty } from "@/components/common";
import { toast } from "sonner";
import { CheckCircle2, Circle, Coins } from "lucide-react";

export default function Incentives() {
  const { filters } = useApp();
  const [month, setMonth] = useState(monthISO());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const params = { month };
    if (filters.branch !== "all") params.branch = filters.branch;
    api.get("/incentive", { params }).then(({ data }) => setRows(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month, filters.branch]);

  const toggle = async (r) => {
    try {
      await api.post("/incentive/approve", { month, technician_id: r.technician_id, approved: !r.approved });
      setRows((rs) => rs.map((x) => x.technician_id === r.technician_id ? { ...x, approved: !x.approved } : x));
      toast.success(!r.approved ? "Incentive approved" : "Approval revoked");
    } catch (e) { toast.error("Failed"); }
  };

  const total = rows.reduce((s, r) => s + r.total, 0);
  const upsellTotal = rows.reduce((s, r) => s + r.upsell_bonus, 0);

  return (
    <div className="animate-fade-up" data-testid="incentives-page">
      <PageHeader title="Monthly Performance & Incentive Summary" subtitle="Auto-calculated per technician — incl. cross-sell/upsell value bonus. Editable slabs in Settings."
        action={<input data-testid="incentive-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)}
          className="bg-[#0f172a] border border-white/10 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#C9A24B] focus:outline-none" />} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="p-5"><p className="text-xs uppercase tracking-wider text-slate-400">Total Incentive Pool</p><p className="text-2xl font-heading text-[#C9A24B] mt-2">{fmt(total)}</p></Card>
        <Card className="p-5"><p className="text-xs uppercase tracking-wider text-slate-400">Cross-sell Bonus</p><p className="text-2xl font-heading text-emerald-400 mt-2">{fmt(upsellTotal)}</p></Card>
        <Card className="p-5"><p className="text-xs uppercase tracking-wider text-slate-400">Technicians</p><p className="text-2xl font-heading mt-2">{rows.length}</p></Card>
        <Card className="p-5"><p className="text-xs uppercase tracking-wider text-slate-400">Approved</p><p className="text-2xl font-heading text-emerald-400 mt-2">{rows.filter((r) => r.approved).length}/{rows.length}</p></Card>
      </div>

      <Card className="p-0 overflow-hidden">
        {loading ? <Empty text="Loading…" /> : rows.length === 0 ? <Empty text="No data for this month." /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-400 text-xs uppercase tracking-wider bg-white/5">
                  {["Technician", "Branch", "Att %", "Proc.", "Dosing", "Proc Bonus", "Zero-Error", "Quality", "Attendance", "Cross-sell", "Reviews", "Testimonials", "Total", "Approve"].map((h) => <th key={h} className="py-3 px-3">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.technician_id} className="border-t border-white/5 hover:bg-white/5" data-testid={`incentive-row-${r.technician_id}`}>
                    <td className="px-3 py-2.5">{r.name}</td>
                    <td className="px-3 text-slate-400">{r.branch}</td>
                    <td className="px-3">{r.attendance_pct}%</td>
                    <td className="px-3">{r.procedures}</td>
                    <td className={`px-3 font-semibold ${r.dosing_errors > 0 ? "text-[#DC2626]" : "text-emerald-400"}`}>{r.dosing_errors}</td>
                    <td className="px-3 text-slate-300">{fmt(r.proc_bonus)}</td>
                    <td className="px-3 text-slate-300">{fmt(r.zero_bonus)}</td>
                    <td className="px-3 text-slate-300">{fmt(r.quality_bonus)}</td>
                    <td className="px-3 text-slate-300">{fmt(r.att_bonus)}</td>
                    <td className="px-3 text-[#C9A24B] font-medium">{fmt(r.upsell_bonus)}</td>
                    <td className="px-3 text-emerald-300">{fmt(r.reviews_bonus)}</td>
                    <td className="px-3 text-emerald-300">{fmt(r.testimonials_bonus)}</td>
                    <td className="px-3 font-bold text-emerald-400">{fmt(r.total)}</td>
                    <td className="px-3">
                      <button data-testid={`approve-${r.technician_id}`} onClick={() => toggle(r)} className="text-slate-400 hover:text-emerald-400">
                        {r.approved ? <CheckCircle2 className="text-emerald-400" size={20} /> : <Circle size={20} />}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
